import { TFunction } from 'i18next'
import { Color } from '@/components/GokuDataGrid/CellStatus'

// ================== Affiliate Status ==================

export const AFFILIATE_STATUS_OPTIONS = (t: TFunction) => [
    {
        label: t('pending'),
        value: 'pending',
        color: 'warning' as Color,
    },
    {
        label: t('active'),
        value: 'active',
        color: 'success' as Color,
    },
    {
        label: t('suspended'),
        value: 'suspended',
        color: 'error' as Color,
    },
    {
        label: t('inactive'),
        value: 'inactive',
        color: 'default' as Color,
    },
]

// ================== Affiliate Tier ==================

export const AFFILIATE_TIER_OPTIONS = (t: TFunction) => [
    {
        label: t('standard'),
        value: 'standard',
        color: 'default' as Color,
    },
    {
        label: t('silver'),
        value: 'silver',
        color: 'info' as Color,
    },
    {
        label: t('gold'),
        value: 'gold',
        color: 'warning' as Color,
    },
    {
        label: t('platinum'),
        value: 'platinum',
        color: 'success' as Color,
    },
]

// ================== Payout Status ==================

export const PAYOUT_STATUS_OPTIONS = (t: TFunction) => [
    {
        label: t('pending'),
        value: 'pending',
        color: 'warning' as Color,
    },
    {
        label: t('approved'),
        value: 'approved',
        color: 'info' as Color,
    },
    {
        label: t('processing'),
        value: 'processing',
        color: 'info' as Color,
    },
    {
        label: t('completed'),
        value: 'completed',
        color: 'success' as Color,
    },
    {
        label: t('failed'),
        value: 'failed',
        color: 'error' as Color,
    },
    {
        label: t('cancelled'),
        value: 'cancelled',
        color: 'default' as Color,
    },
]

// ================== Commission Status ==================

export const COMMISSION_STATUS_OPTIONS = (t: TFunction) => [
    {
        label: t('pending'),
        value: 'pending',
        color: 'warning' as Color,
    },
    {
        label: t('confirmed'),
        value: 'confirmed',
        color: 'success' as Color,
    },
    {
        label: t('refunded'),
        value: 'refunded',
        color: 'error' as Color,
    },
]

export const getCommissionStatusFilterOptions = (t: TFunction) => [
    { label: t('pending'), value: 'pending' },
    { label: t('confirmed'), value: 'confirmed' },
    { label: t('refunded'), value: 'refunded' },
]

// The tier -> commission-percent table that used to live here was a client-side invention:
// the API has no such mapping (tier is a label only, never read by the commission engine) and
// already owns the default rate. Worse, it is a *fallback* rate that a matching commission
// policy overrides — and a global policy applies to every affiliate by default, so it is
// rarely the rate anyone is actually paid. Leave the field blank and let the API decide.

// ================== Default Payout Threshold ==================

export const DEFAULT_PAYOUT_THRESHOLD = 1000000 // 1M VND

// ================== Filter Options (for FormItem select) ==================

export const getAffiliateStatusFilterOptions = (t: TFunction) => [
    { label: t('pending'), value: 'pending' },
    { label: t('active'), value: 'active' },
    { label: t('suspended'), value: 'suspended' },
    { label: t('inactive'), value: 'inactive' },
]

export const getAffiliateTierFilterOptions = (t: TFunction) => [
    { label: t('standard'), value: 'standard' },
    { label: t('silver'), value: 'silver' },
    { label: t('gold'), value: 'gold' },
    { label: t('platinum'), value: 'platinum' },
]

export const getPayoutStatusFilterOptions = (t: TFunction) => [
    { label: t('pending'), value: 'pending' },
    { label: t('approved'), value: 'approved' },
    { label: t('processing'), value: 'processing' },
    { label: t('completed'), value: 'completed' },
    { label: t('failed'), value: 'failed' },
    { label: t('cancelled'), value: 'cancelled' },
]

// ================== Commission Policy Scope ==================

export const COMMISSION_POLICY_SCOPE_OPTIONS = (t: TFunction) => [
    {
        label: t('scope_default'),
        value: 'default',
        color: 'default' as Color,
    },
    {
        label: t('scope_product_group'),
        value: 'product_group',
        color: 'info' as Color,
    },
    {
        label: t('scope_product'),
        value: 'product',
        color: 'success' as Color,
    },
]

export const getCommissionPolicyScopeFilterOptions = (t: TFunction) => [
    { label: t('scope_default'), value: 'default' },
    { label: t('scope_product_group'), value: 'product_group' },
    { label: t('scope_product'), value: 'product' },
]

export const POLICY_ACTIVE_STATUS_OPTIONS = (t: TFunction) => [
    { label: t('active'), value: 'true' },
    { label: t('inactive'), value: 'false' },
]

// ================== Match Reason Labels ==================

export const MATCH_REASON_LABELS = (t: TFunction): Record<string, string> => ({
    product: t('match_reason_product'),
    product_group: t('match_reason_product_group'),
    affiliate_default: t('match_reason_affiliate_default'),
    global_default: t('match_reason_global_default'),
    affiliate_fallback: t('match_reason_affiliate_fallback'),
})

// ================== Pending Invoice Status ==================

export const getPendingInvoiceStatusFilterOptions = (t: TFunction) => [
    { label: t('invoice_status_waiting-confirm'), value: 'waiting-confirm' },
    { label: t('invoice_status_approved'), value: 'approved' },
    { label: t('invoice_status_rejected'), value: 'rejected' },
    { label: t('invoice_status_done'), value: 'done' },
]
